import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import Keycloak from 'keycloak-js';
import { KeycloakService } from 'keycloak-angular';
import { Observable, from, switchMap, throwError } from 'rxjs';
import { API_URL, SESSION_AUTO_REFRESH_ELAPSED_RATIO } from '../config/general.config';
import { getSessionLength } from '../utils/session.utils';

const isApiRequest = (url: string): boolean =>
  url.startsWith(API_URL) || url.startsWith(`${window.location.origin}${API_URL}`);

const isOnline = (): boolean =>
  localStorage.getItem('forcedOfflineMode') !== 'true' && navigator.onLine;

const shouldRefreshSession = (keycloak: Keycloak): boolean => {
  const refreshToken = keycloak.refreshTokenParsed;

  if (!refreshToken?.iat || !refreshToken.exp) {
    return false;
  }

  const sessionLength = getSessionLength(refreshToken);
  if (sessionLength <= 0) {
    return false;
  }

  const currentTimeInSeconds = Math.floor(Date.now() / 1000);
  const elapsedSeconds = currentTimeInSeconds - refreshToken.iat;
  const refreshThreshold = sessionLength * SESSION_AUTO_REFRESH_ELAPSED_RATIO;

  return elapsedSeconds >= refreshThreshold;
};

let refreshPromise: Promise<boolean> | null = null;

@Injectable()
export class SessionRefreshInterceptor implements HttpInterceptor {
  constructor(private readonly injector: Injector) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const keycloakService = this.injector.get(KeycloakService);
    const keycloak = keycloakService.getKeycloakInstance();

    if (!isOnline() || !isApiRequest(req.url) || !shouldRefreshSession(keycloak)) {
      return next.handle(req);
    }

    if (refreshPromise === null) {
      refreshPromise = keycloak.updateToken(-1)
        .then(() => true)
        .catch(() => {
          void keycloak.logout();
          return false;
        })
        .finally(() => {
          refreshPromise = null;
        });
    }

    return from(refreshPromise).pipe(
      switchMap((refreshSucceeded) => {
        if (!refreshSucceeded) {
          return throwError(() => new Error('Session refresh failed.'));
        }

        return next.handle(req);
      })
    );
  }
}
